import { manageApiCall } from "@/api/api-manager";
import { cloneMediaForFrages, disconnectAndDeleteFragenblockRecursive, postFragenblock, putFragenblock, replaceFragenblock, disconnectImageFromFrage } from "@/api/fragenblock-api";
import { createAsyncQueue } from "@/api/helper/async-queue";
import { getFragenblockWithIndices } from "@/composables/Bestandsaufnahme/useIdentifierFinder";
import Bestandsaufnahme from "@/models/ba/Bestandsaufnahme";
import { Frage } from "@/models/ba/Frage";
import { Fragenblock } from "@/models/ba/Fragenblock";
import BestandsaufnahmeModel from "@/models/ba/models/bestandsaufnahme.model";
import { inputFieldKeys } from "@/utilities/constants";
import { alertCopyFragenblock } from "@/utilities/copy-fragenblock-alert";
import { Monitoring } from "@/utilities/monitoring";
import { presentToast } from "@/utilities/toast-helper";

export const fragenStore = {
    namespaced: true,
    state: () => ({
        queuePostFragenblock: createAsyncQueue(),
        queuePutFragenblock: createAsyncQueue(),
        queueDisconnectAndDeleteFragenblock: createAsyncQueue(),
        socket: null,
    }),
    mutations: {
        setSocket(state: any, socket: any) {
            state.socket = socket; // TODO: refactor after socket is not in a composable anymore
        }
    },
    actions: {

        async createFragenblockFromFeature(
            { commit, state, rootState }: any,
            { ba, parentFragenblock, drawItem, userId }: { ba: Bestandsaufnahme, parentFragenblock: Fragenblock, drawItem: any, userId: number; }
        ) {

            if (!parentFragenblock.id) {
                Monitoring.withScope(scope => {
                    scope.setContext("Function parameters", { SurveyId: ba.id, Path: parentFragenblock.identifier });
                    Monitoring.error("Unexpected Error while creating Fragenblock from draw feature: parentFragenblock has no id");
                });
                return null;
            }

            await parentFragenblock.addFragenblockInstance(parentFragenblock.fragenblocks[0]);
            const newlyCreated = parentFragenblock.fragenblocks[parentFragenblock.fragenblocks.length - 1];

            newlyCreated.config = {
                ...newlyCreated.config,
                userId
            };
            newlyCreated.createdAt = new Date().toISOString();

            if (newlyCreated.frages && newlyCreated.frages.length > 0) {
                const firstFrage = newlyCreated?.frages[0];
                firstFrage.eingabeText = newlyCreated.getTitle() + " - " + (parentFragenblock.fragenblocks.length - 1).toString();
            }
            newlyCreated.geoJson = drawItem;

            const fallbackValue = { data: { fragenblock: newlyCreated } };

            const apiCall = manageApiCall(postFragenblock, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                retryOptions: { retries: 5 },
                queue: state.queuePostFragenblock,
                errorMessage: "Error while creating Fragenblock from draw feature",
                failHandlers: {
                    fallbackValue,
                    onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Creating Fragenblock from draw feature timed out",
                    onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected && !!parentFragenblock.id,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba), //TODO: do something else?
                    fallbackValue,
                }
            });

            const result = await apiCall.makeRequest({
                newFragenblock: await newlyCreated.toClassJson(),
                parentFragenblockId: parentFragenblock.id,
            });

            if (result?.data?.fragenblock?.id) {
                newlyCreated.injectIds(result.data.fragenblock);
            }
            return newlyCreated;
        },

        async addFragenblock(
            { commit, state, rootState }: any,
            { ba, parentFragenblock, fragenblock }: { ba: Bestandsaufnahme, parentFragenblock: Fragenblock, fragenblock: Fragenblock; }
        ) {
            if (!parentFragenblock.id) {
                Monitoring.withScope(scope => {
                    scope.setContext("Function parameters", { SurveyId: ba.id, ParentId: parentFragenblock.id });
                    Monitoring.error("Unexpected Error while adding Fragenblock: parentFragenblock has no id");
                });
                return null;
            }

            await parentFragenblock.addFragenblockInstance(fragenblock);
            const newFragenblock = parentFragenblock.fragenblocks[parentFragenblock.fragenblocks.length - 1];

            const fallbackValue = { data: { fragenblock: newFragenblock } };

            const apiCall = manageApiCall(postFragenblock, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                retryOptions: { retries: 5 },
                queue: state.queuePostFragenblock,
                errorMessage: "Error while adding Fragenblock",
                failHandlers: {
                    fallbackValue,
                    onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Adding Fragenblock timed out",
                    onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    fallbackValue
                }
            });

            const result = await apiCall.makeRequest({
                newFragenblock: await newFragenblock.toClassJson(),
                parentFragenblockId: parentFragenblock.id,
            });

            if (result?.data?.fragenblock?.id) {
                newFragenblock.injectIds(result.data.fragenblock);
            }
            return newFragenblock;
        },

        async duplicateFragenblock(
            { commit, state, rootState }: any,
            { ba, parentFragenblock, fragenblock }: { ba: Bestandsaufnahme, parentFragenblock: Fragenblock, fragenblock: Fragenblock; }
        ) {
            if (!parentFragenblock.id) {
                Monitoring.withScope(scope => {
                    scope.setContext("Function parameters", { SurveyId: ba.id, ParentId: parentFragenblock.id, FragenblockToDuplicateId: fragenblock.id });
                    Monitoring.error("Unexpected Error while duplicating Fragenblock: Missing id");
                });
                return null;
            }

            await parentFragenblock.duplicateFragenblockInstance(fragenblock);
            const duplicatedFragenblock = parentFragenblock.fragenblocks[parentFragenblock.fragenblocks.length - 1];

            const fallbackValue = { data: { fragenblock: duplicatedFragenblock } };

            const apiCall = manageApiCall(postFragenblock, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                retryOptions: { retries: 5 },
                queue: state.queuePostFragenblock,
                errorMessage: "Error while duplicating Fragenblock",
                failHandlers: {
                    fallbackValue,
                    onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Duplicating Fragenblock timed out",
                    onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    fallbackValue
                }
            });

            const result = await apiCall.makeRequest({
                newFragenblock: await duplicatedFragenblock.toClassJson(),
                parentFragenblockId: parentFragenblock.id,
            });

            if (result?.data?.fragenblock?.id) {
                duplicatedFragenblock.injectIds(result.data.fragenblock);
            }
            return duplicatedFragenblock;
        },

        async updateFragenblock(
            { commit, state, rootState }: any,
            { ba, fragenblock, updateFields }: { ba: Bestandsaufnahme, fragenblock: Fragenblock, updateFields: Record<string, any>; }
        ) {
            const fallbackValue = { data: { fragenblock } };

            const apiCall = manageApiCall(putFragenblock, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                retryOptions: { retries: 5 },
                queue: state.queuePutFragenblock,
                errorMessage: "Error updating Fragenblock",
                failHandlers: {
                    fallbackValue,
                    onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Updating Fragenblock timed out",
                    onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    fallbackValue
                }
            });

            const result = await apiCall.makeRequest({ fragenblockFields: { ...updateFields, id: fragenblock.id } });
            return result?.data?.fragenblock;
        },

        async disconnectAndDeleteFragenblock(
            { commit, state, rootState }: any,
            { ba, parentFragenblock, fragenblock }: { ba: Bestandsaufnahme, parentFragenblock: Fragenblock, fragenblock: Fragenblock; }
        ) {
            if (!parentFragenblock.id || (!fragenblock.id && !fragenblock.uid)) {
                Monitoring.withScope(scope => {
                    scope.setContext("Function parameters", { SurveyId: ba.id, ParentId: parentFragenblock.id, FragenblockToDeleteId: fragenblock.id });
                    Monitoring.error("Unexpected Error while disconnecting and deleting Fragenblock: Missing id");
                });
                return null;
            }

            await parentFragenblock.deleteFragenblockInstance(fragenblock);

            if (!fragenblock.id) {
                return null;
            }

            const apiCall = manageApiCall(disconnectAndDeleteFragenblockRecursive, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                retryOptions: { retries: 5 },
                errorMessage: "Error disconnecting Fragenblock",
                queue: state.queueDisconnectAndDeleteFragenblock,
                failHandlers: {
                    fallbackValue: null,
                    onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Disconnecting Fragenblock timed out",
                    onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    fallbackValue: null,
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                }
            });

            apiCall.makeRequest({ id: fragenblock.id, parentId: parentFragenblock.id });
        },

        async cloneMedia(
            { state, rootState }: any,
            { media }: { media: any[]; }
        ) {
            if (!media || media.length === 0) {
                Monitoring.withScope(scope => {
                    scope.setContext("Function parameters", { Media: media });
                    Monitoring.error("Missing media to clone");
                });
                return { failed: true };
            }

            const apiCall = manageApiCall(cloneMediaForFrages, {
                retryOptions: { retries: 5 },
                errorMessage: "Error cloning media",
                queue: state.queuePostFragenblock,
                failHandlers: {
                    fallbackValue: { failed: true },
                    // for every fragenblock input change first "saveLocally" in RecursiveForm.vue and then 
                    // "store.dispatch("currentHzba/saveHzbaLocally")"" is called which sets hasUnsavedChanges to true
                    // not setting markWithUnsavedChanges here for this reason
                    //onFail: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                timeout: {
                    time: 35000,
                    message: "Cloning media timed out",
                    //onTimeout: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba)
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    //onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    fallbackValue: { noConnection: true }
                }
            });

            const result = await apiCall.makeRequest({ media });
            return result?.data;
        },

        async replaceFragenblocks(
            { state, commit, rootState }: any,
            { survey, parent, newFragenblocks, modalDepth }:
                { survey: Bestandsaufnahme, parent: Fragenblock, newFragenblocks: Fragenblock[]; modalDepth: number; }
        ) {
            if (!parent.id) {
                parent.fragenblocks = newFragenblocks;
                return;
            }

            const onFail = () => {
                presentToast(rootState.app.t("toasts.copyFragenblock.failed"));
                BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", survey);
            };

            const apiCall = manageApiCall(replaceFragenblock, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                errorMessage: "Error replacing Fragenblock",
                queue: state.queuePostFragenblock,
                failHandlers: {
                    fallbackValue: null,
                    onFail,
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Replacing Fragenblock timed out",
                    onTimeout: onFail
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", survey),
                    fallbackValue: { noConnection: true }
                }
            });

            const result = await apiCall.makeRequest({
                newFragenblockData: await Promise.all(newFragenblocks.map((fragenblock: Fragenblock) => fragenblock.toClassJson())),
                id: parent.id
            });

            const fragenblocks = result?.data?.fragenblock?.fragenblocks;
            if (fragenblocks && fragenblocks.length > 0) {
                parent.fragenblocks = fragenblocks.map((fragenblock: any) =>
                    new Fragenblock(fragenblock, parent.path, modalDepth, parent.pathWithMultiFragenblockIndex));
            } else if (result.noConnection) {
                parent.fragenblocks = newFragenblocks;
            }
        },

        async copyFrages(
            { state, rootState, dispatch }: any,
            { targetFragenblock, sourceFragenblock }:
                { survey: Bestandsaufnahme, targetFragenblock: Fragenblock, sourceFragenblock: Fragenblock; }
        ) {
            const mediaClones: any[] = [];
            sourceFragenblock?.frages?.forEach((sourceFrage: Frage, index: number) => {
                if (!targetFragenblock?.frages || !targetFragenblock?.frages[index]) {
                    return;
                }

                Object.keys(sourceFrage).forEach((key: string) => {
                    if (inputFieldKeys.includes(key) && targetFragenblock.frages && targetFragenblock.frages[index]) {
                        if (!targetFragenblock.frages[index].config.readOnly) {
                            if (key === "eingabeMedien") {
                                const mediaValue = sourceFrage[key];
                                const targetFrage = targetFragenblock.frages[index];
                                if (mediaValue && mediaValue.length > 0) {
                                    if (rootState.app.networkConnected) {
                                        mediaClones.push({
                                            frageId: targetFrage.id,
                                            field: key,
                                            media: mediaValue.filter((media: any) => !media.webPath).map((media: any) => {
                                                return {
                                                    url: media.url,
                                                    mime: media.mime,
                                                    name: media.name,
                                                };
                                            })
                                        });
                                    } else {
                                        const duplicatedLocalPhotos = mediaValue.filter((media: any) => media.webPath).map((media: any) => {
                                            return {
                                                ...media
                                            };
                                        });
                                        targetFrage[key] = [...targetFrage[key], ...duplicatedLocalPhotos];
                                    }
                                }
                            } else {
                                // @ts-ignore Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Frage'.
                                targetFragenblock.frages[index][key] = sourceFrage[key];
                            }
                        }
                    }
                });
            });
            // TODO: socket not an optimal solution, but leaving it to not introduce new errors
            // Alternative ideas: use REST API to create a whole new fragenblock or use rest api to update all fragen in one go
            state.socket?.handleSocketEmit(targetFragenblock.frages as Frage[]);

            // The initial idea was to overwrite the fragenblock client entity and to send a socket event with
            // an array of frage-data to "simulate" a form input change. at this time the fragenblock endpoints did not exist.
            // But for media fields this way the media item is not cloned. With cloning on server side a response to the client
            // is required why I added a cloneMedia endpoint.
            // TODO: clone all data via one endpoint that uses the cloneMedia service in the backend.
            if (mediaClones.length > 0) {
                dispatch("cloneMedia", { media: mediaClones })
                    .then((data: any) => {
                        data?.clonedMedia?.forEach((media: any) => {
                            const frage = targetFragenblock.frages?.find((frage: Frage) => frage.id === media.frageId);
                            if (frage) {
                                // @ts-ignore Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Frage'.
                                frage[media.field] = [...frage[media.field], ...media.media];
                            }
                        });
                    });
            }
        },

        async copyFragenblock(
            { dispatch, rootState }: any,
            { survey, targetFragenblock, sourceSurveyId, modalDepth }:
                { survey: Bestandsaufnahme, targetFragenblock: Fragenblock, sourceSurveyId: string; modalDepth: number; }
        ) {
            try {
                const sourceBa: any = await BestandsaufnahmeModel.dispatch("retrieveCompleteSurvey", sourceSurveyId);
                const sourceBaClass = new Bestandsaufnahme(sourceBa);
                const sourceFragenblock = getFragenblockWithIndices(sourceBaClass, targetFragenblock.pathWithMultiFragenblockIndex);

                if (!sourceFragenblock) {
                    presentToast(rootState.app.t("toasts.copyFragenblock.noSourceFragenblock"));
                    return;
                }

                /*
                  4 cases:
                  1. sourceFragenblock.isTemplate(): the clicked element is a template (=non-displayed first element of list), copy the fragenblocks from the parent
                  2. sourceFragenblock.hasMehrfachFragenblock(): copy the fragenblocks property
                  3. sourceFragenblock.isInstanceOfMultiple(): usually instance has another fragenblock as child, copy the fragenblocks property as well 
                  4. sourceFragenblock.hasFrages(): copy the fragen
                */

                if (sourceFragenblock.isTemplate()) {
                    const sourceFragenblockParent = getFragenblockWithIndices(
                        sourceBaClass,
                        targetFragenblock.parentPathWithMultiFragenblockIndex ?? targetFragenblock.parentPath
                    );

                    if (!sourceFragenblockParent) {
                        presentToast(rootState.app.t("toasts.copyFragenblock.noSourceFragenblock"));
                        return;
                    }

                    alertCopyFragenblock(async () => {
                        const targetFragenblockParent = getFragenblockWithIndices(
                            survey,
                            targetFragenblock.parentPathWithMultiFragenblockIndex ?? targetFragenblock.parentPath
                        );
                        const fragenblockCopies = await Promise.all(sourceFragenblockParent.fragenblocks.map(f => f.copySelf()));

                        await dispatch("replaceFragenblocks", {
                            survey,
                            parent: targetFragenblockParent,
                            newFragenblocks: fragenblockCopies,
                            modalDepth
                        });
                    });
                } else if (sourceFragenblock.hasMehrfachFragenblock() || sourceFragenblock.isInstanceOfMultiple()) {
                    alertCopyFragenblock(async () => {
                        const fragenblocks = await Promise.all(sourceFragenblock.fragenblocks.map(f => f.copySelf(
                            targetFragenblock.path,
                            targetFragenblock.pathWithMultiFragenblockIndex,
                            modalDepth
                        )));
                        await dispatch("replaceFragenblocks", {
                            survey,
                            parent: targetFragenblock,
                            newFragenblocks: fragenblocks,
                            modalDepth
                        });
                    });
                }

                if (sourceFragenblock.hasFrages()) {
                    await dispatch("copyFrages", {
                        survey,
                        targetFragenblock,
                        sourceFragenblock
                    });
                }
            } catch (error: any) {
                Monitoring.chainError("Error while copying Fragenblock", error);
            }
        },

        // Disconnecting is not required to work offline, as the existing images are not editable offline.
        // Only offline added image are able to be edited offline.
        async disconnectImage(
            { state, commit, rootState }: any,
            { ba, frageId, oldImageId }:
                { ba: Bestandsaufnahme, frageId: number, oldImageId: number }
        ) {

            const onFail = () => {
                presentToast(rootState.app.t("toasts.disconnectImage.failed"));
                BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba);
            };

            const apiCall = manageApiCall(disconnectImageFromFrage, {
                onStart: () => commit('app/setLoadingSpinner', true, { root: true }),
                errorMessage: "Error disconnecting image from Frage",
                queue: state.queuePutFragenblock,
                failHandlers: {
                    fallbackValue: null,
                    onFail,
                    onFinally: () => commit('app/setLoadingSpinner', false, { root: true })
                },
                timeout: {
                    time: 35000,
                    message: "Disconnect old image timed out",
                    onTimeout: onFail
                },
                preRequestCheck: {
                    check: () => rootState.app.networkConnected,
                    onAbort: () => BestandsaufnahmeModel.dispatch("markWithUnsavedChanges", ba),
                    fallbackValue: { noConnection: true }
                }
            });

            await apiCall.makeRequest({
                frageId: frageId,
                imageId: oldImageId
            });
        },
    }
};
